<template>
    <div class="diligence" :class="readonly ? 'diligence--readonly' : 'diligence--editable'">
        <div
          v-for="option in visibleOptions"
          class="input-group diligence__item"
          :class="{ 'diligence__item--active': diligence === option.value}">
            <input type="radio" :id="option.tag + _uid" :value="option.tag" v-model="diligence" class="diligence__radio" v-if="!readonly">
            <label :for="option.tag + _uid" class="diligence__label">
                <span class="diligence__icon diligence__icon--inline" :class="iconClass(option)">
                    <img :src="spriteImage" class="inline-icon" draggable="false"/>
                </span>
                {{ option.text }} <em>{{ option.subtext }}</em>
            </label>
        </div>
    </div>
</template>


<script>

import api from 'js/api/resources'
import { transform } from 'js/vuex/helpers'
import sprite from 'js/helpers/sprite'
import notify from 'js/components/notifications/notificationsMixin'
import track from 'js/helpers/analytics'

export default {
    name: 'Diligence',
    props: {
        progress: {},
        isDemo: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        showOnlySelected: {
            type: Boolean,
            default: false,
        }
    },

    mixins: [ notify ],
    components: {},

    data: function() {
        return {
            isUpdating: false,
            diligence: null,
        }
    },

    vuex: {
        actions: {
            patchDiligence: ({ dispatch }, progress, diligence) => {

                return api.patchDiligence(progress.id, diligence)
                    .then( response => transform(response.body) )
                    .then( result => {
                        const diligence = result.diligence[0]
                        dispatch('ADD_DILIGENCE', result.diligence || [])
                        dispatch('ADD_PROGRESS', result.progress || [])
                        return diligence
                    })
            },
            fakeDiligence: ({ dispatch }, progress, diligence) => {

                const fake = {
                    id: 'fake-' + progress.id,
                    tag: diligence,
                }

                dispatch('ADD_DILIGENCE', [fake])
                dispatch('ADD_DILIGENCE_TO_PROGRESS', progress.id, fake.id)
            }
        }
    },

    computed: {

        options: function() {

            return [
                {
                    tag: 'sufficient',
                    text: 'Je hebt de opdracht met aandacht gemaakt.',
                    value: 'sufficient',
                },
                {
                    tag: 'poor',
                    text: 'Je hebt de opdracht met weinig aandacht gemaakt.',
                    subtext: '(Als je de opdracht niet begrijpt, of als je het doel van de opdracht niet begrijpt, vraag het dan aan een klasgenoot of je docent of geef je op voor een werkbespreking.)',
                    value: 'poor',
                },
                {
                    tag: 'fail',
                    text: 'Je hebt de opdracht niet met aandacht gemaakt.',
                    subtext: '(Neem contact op met je docent.)',
                    value: 'fail',
                },
            ]
        },

        visibleOptions: function() {
            if ( this.showOnlySelected ) {
                return this.options.filter( o => o.tag === this.diligence )
            }

            return this.options
        },

        spriteImage: function() {
            return sprite('sprite-main@2x.png')
        },

    },

    methods: {

        iconClass: function(option) {

            var cls = 'diligence__icon--'+option.tag
            if ( this.diligence === option.value ) {
                cls += ' active'
            }

            return cls

        },

        store: function(newVal, oldVal) {
            if ( this.readonly || this.isUpdating ) {
                return
            }

            this.$emit('input', this.diligence)

            if ( this.isDemo ) {
                return this.fakeDiligence(this.progress, newVal)
            }

            this.isUpdating = true

            this.patchDiligence(this.progress, newVal)
                .catch( err => {
                    track.exception('Could not store diligence', err)
                    this.diligence = oldVal
                })
                .then( () => { this.isUpdating = false })

        },

        setDiligence: function() {
            this.diligence = this.progress.diligence == null ? null
                : this.$store.state.diligence.storage[ this.progress.diligence[0] ].tag
            this.$emit('input', this.diligence)
        },

    },

    created: function() {
        this.setDiligence()
        this.$watch( 'diligence', this.store)
        this.$watch( 'progress.diligence', this.setDiligence)
    },

}
</script>


<style lang="scss">

    @import 'global';

    .diligence__radio {
        display: none;
    }

    .diligence__icon {
        display: block;
        float: left;
        margin-right: 10px;
        margin-top: -4px;
        // -webkit-print-color-adjust: exact;

        @media print {
            transform: scale(0.6);
        }

        // needed for inline-sprite
        position: relative;
        overflow: hidden;

        @include sprite-width($rating-icon-neutral);
        @include sprite-height($rating-icon-neutral);


        &--inline {

            .inline-icon {
                @include sprite-inline($rating-icon-neutral);
                margin: 0 !important;
            }

            @each $type, $value in $diligence-types {
                &.diligence__icon--#{$type} {
                    .inline-icon {
                        @include sprite-inline-position(nth($value, 1));
                    }

                    &.active {
                        .inline-icon {
                            @include sprite-inline-position(nth($value, 2));
                        }
                    }
                }
            }

        }

        &--bg-replace {

            @include sprite-image($rating-icon-neutral-2x); // only change this to get retina/non-retina
            @include sprite-background-size($rating-icon-neutral); // always non-retina

            @each $type, $value in $diligence-types {
                &.diligence__icon--#{$type} {
                    @include sprite-position(nth($value, 1)); // always non-retina

                    &.active {
                        @include sprite-position(nth($value, 2)); // always non-retina
                    }
                }
            }

        }

    }

    .diligence__label {
        display: block;
    }

    .diligence--readonly {
      .diligence__item {
        display: none;
        &--active {
          display: block;
        }
      }
    }

    .diligence--editable {

        .diligence__label {
            &:hover {
                cursor: pointer;

                .diligence__icon {

                    &--inline {

                        @each $type, $value in $diligence-types {
                            &.diligence__icon--#{$type} {
                                .inline-icon {
                                    @include sprite-inline-position(nth($value, 2));
                                }
                            }

                        }

                    }

                    &--bg-replace {

                        @each $type, $value in $diligence-types {
                            &.diligence__icon--#{$type} {
                                @include sprite-position(nth($value, 2)); // always non-retina
                            }

                        }

                    }

                }
            }

        }

    }
</style>



// WEBPACK FOOTER //
// Diligence.vue?162c5d1a